import AppModal from '@/components/atoms/AppModal';
import { AppButton } from '@/components/atoms';
import RezeptSheetFormFields from '@/components/molecules/RezeptSheetFormFields';
import { useAdminRezeptSheetDetail } from '@/hooks/admin/useAdminRezeptSheetDetail';
import { useUpdateRezeptSheet } from '@/hooks/admin/useUpdateRezeptSheet';
import useAppToast from '@/hooks/useAppToast';
import { createRezeptSheetSchema, type CreateRezeptSheetFormData } from '@/schemas/rezeptSheetSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';

import { useEffect } from 'react';
import { Spin } from 'antd';

interface EditRezeptSheetModalProps {
  id: number | null;
  isOpen: boolean;
  onClose: () => void;
}

const EditRezeptSheetModal = ({ id, isOpen, onClose }: EditRezeptSheetModalProps) => {
  const { data: detail, isLoading: detailLoading, isError: detailError } = useAdminRezeptSheetDetail(isOpen ? id : null);
  const { updateRezeptSheet, isPending: updatePending } = useUpdateRezeptSheet();
  const toast = useAppToast();

  const {
    handleSubmit,
    reset,
    control,
    clearErrors,
    formState: { errors },
  } = useTrimmedForm<CreateRezeptSheetFormData>({
    resolver: zodResolver(createRezeptSheetSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (detail) {
      reset({
        service_type: detail.service_type ?? '',
        sheet_name: detail.sheet_name ?? '',
        unit_amount: detail.unit_amount != null ? String(detail.unit_amount) : '',
        price: detail.price != null ? String(detail.price) : '',
        sortno: detail.sortno != null ? String(detail.sortno) : '',
        available_from: detail.available_from ?? '',
        available_to: detail.available_to === '2100-12-31' ? '' : (detail.available_to ?? ''),
      });
    }
  }, [detail, reset]);

  useEffect(() => {
    if (detailError) {
      onClose();
    }
  }, [detailError, onClose]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateRezeptSheetFormData) => {
    if (id == null) return;
    await updateRezeptSheet({
      id,
      data: {
        ...data,
        available_to: data.available_to || null,
      },
    });
    handleClose();
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`レセプト用紙編集:${id ?? ''}`}
      maxWidth="max-w-lg"
      footer={null}
    >
      {detailLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit, () => toast.error('エラーが発生しました。'))} className="space-y-4">
          <RezeptSheetFormFields errors={errors} control={control} clearErrors={clearErrors} />

          <div className="flex justify-end gap-2 pt-2">
            {/* <AppButton onClick={handleClose}>キャンセル</AppButton> */}
            <AppButton
              type="primary"
              htmlType="submit"
              loading={updatePending}
            >
              更新
            </AppButton>
          </div>
        </form>
      )}
    </AppModal>
  );
};

export default EditRezeptSheetModal;
